import React, { useState, useEffect, useRef } from 'react';
import {
  MessageSquarePlus,
  Bug,
  Lightbulb,
  Wrench,
  HelpCircle,
  X,
  Copy,
  Download,
  ExternalLink,
  Check,
  Camera,
  ChevronDown,
  AlertCircle,
  Clock,
  Trash2,
  Send,
  Loader2,
} from 'lucide-react';
import { Button } from './ui/Button';
import {
  FeedbackType,
  FeedbackPriority,
  FeedbackSubmission,
  FeedbackReport,
} from '../types/feedback';
import {
  feedbackContextService,
  getAppContext,
} from '../services/feedback/context';

interface FeedbackButtonProps {
  position?: 'bottom-right' | 'bottom-left';
}

export const FeedbackButton: React.FC<FeedbackButtonProps> = ({
  position = 'bottom-right',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'new' | 'history'>('new');
  const [reports, setReports] = useState<FeedbackReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<FeedbackReport | null>(null);
  const [copied, setCopied] = useState(false);
  const [sendingToCursor, setSendingToCursor] = useState(false);
  const [cursorStatus, setCursorStatus] = useState<{ 
    success?: boolean; 
    message?: string; 
    error?: string;
    agentId?: string;
    agentUrl?: string;
    agentName?: string;
    branchName?: string;
    prUrl?: string;
  } | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<FeedbackSubmission>({
    type: 'BUG',
    priority: 'MEDIUM',
    title: '',
    description: '',
    steps: '',
    expectedBehavior: '',
    actualBehavior: '',
    includeScreenshot: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Refs for timeout cleanup
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cursorStatusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      if (cursorStatusTimeoutRef.current) clearTimeout(cursorStatusTimeoutRef.current);
    };
  }, []);

  // Load and sync reports when panel opens
  useEffect(() => {
    if (isOpen) {
      // First load local reports immediately for fast display
      setReports(feedbackContextService.getStoredReports());
      
      // Then sync from cloud in background
      feedbackContextService.syncReportsFromCloud?.()
        .then((synced: any[]) => setReports(synced))
        .catch(console.warn);
    }
  }, [isOpen]);

  const resetForm = () => {
    setFormData({
      type: 'BUG',
      priority: 'MEDIUM',
      title: '',
      description: '',
      steps: '',
      expectedBehavior: '',
      actualBehavior: '',
      includeScreenshot: false,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.description) return;

    setIsSubmitting(true);
    try {
      const report = await feedbackContextService.createReport(formData);
      setReports(feedbackContextService.getStoredReports());
      setSelectedReport(report);
      setActiveTab('history');
      resetForm();
    } catch (error) {
      console.error('Failed to create report:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopy = async (report: FeedbackReport) => {
    const success = await feedbackContextService.copyReportToClipboard(report);
    if (success) {
      setCopied(true);
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = (report: FeedbackReport) => {
    feedbackContextService.downloadReport(report);
  };

  const handleOpenGitHub = (report: FeedbackReport) => {
    const url = feedbackContextService.getGitHubIssueUrl(report);
    window.open(url, '_blank');
  };

  const handleDelete = (id: string) => {
    feedbackContextService.deleteReport(id);
    setReports(feedbackContextService.getStoredReports());
    if (selectedReport?.id === id) {
      setSelectedReport(null);
    }
  };

  const handleSendToCursor = async (report: FeedbackReport) => {
    setSendingToCursor(true);
    setCursorStatus(null);
    
    try {
      const result = await feedbackContextService.sendToCursor(report);
      setCursorStatus(result);
      
      if (result.success) {
        // Status is already updated in sendToCursor(), just refresh reports
        setReports(feedbackContextService.getStoredReports());
        
        // Update selected report if it's the one we just sent
        if (selectedReport?.id === report.id) {
          setSelectedReport({ ...selectedReport, status: 'SUBMITTED' });
        }
      }
    } catch (error) {
      setCursorStatus({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setSendingToCursor(false);
      // Clear status message after 5 seconds
      if (cursorStatusTimeoutRef.current) clearTimeout(cursorStatusTimeoutRef.current);
      cursorStatusTimeoutRef.current = setTimeout(() => setCursorStatus(null), 5000);
    }
  };

  const context = getAppContext();

  const typeConfig: Record<FeedbackType, { icon: React.ReactNode; color: string; label: string }> = {
    BUG: { icon: <Bug className="w-4 h-4" />, color: 'text-red-400', label: 'Bug Report' },
    FEATURE: { icon: <Lightbulb className="w-4 h-4" />, color: 'text-warning', label: 'Feature Request' },
    IMPROVEMENT: { icon: <Wrench className="w-4 h-4" />, color: 'text-oler-blue-light', label: 'Improvement' },
    QUESTION: { icon: <HelpCircle className="w-4 h-4" />, color: 'text-purple-400', label: 'Question' },
  };

  const priorityConfig: Record<FeedbackPriority, { color: string; label: string }> = {
    LOW: { color: 'bg-success/20 text-success', label: 'Low' },
    MEDIUM: { color: 'bg-warning/20 text-warning', label: 'Medium' },
    HIGH: { color: 'bg-orange-500/20 text-orange-400', label: 'High' },
    CRITICAL: { color: 'bg-red-500/20 text-red-400', label: 'Critical' },
  };

  const positionClasses = position === 'bottom-right' 
    ? 'right-4 md:right-6' 
    : 'left-4 md:left-6';

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed safe-bottom-20 ${positionClasses} z-50 w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center`}
        aria-label="Report bug or request feature"
      >
        {isOpen ? (
          <X className="w-5 h-5" />
        ) : (
          <MessageSquarePlus className="w-5 h-5" />
        )}
      </button>

      {/* Feedback Panel */}
      {isOpen && (
        <div
          className={`fixed safe-bottom-32 ${positionClasses} z-50 w-[calc(100vw-2rem)] md:w-96 max-h-[70vh] md:max-h-[85vh] bg-[var(--panel-bg)] border border-[color:var(--border)] rounded-xl shadow-2xl overflow-hidden`}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-b border-[color:var(--border)] p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-[var(--text)] flex items-center gap-2">
                <MessageSquarePlus className="w-5 h-5 text-purple-400" />
                Feedback
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-dim px-2 py-1 rounded bg-[var(--surface-bg)]">
                  📍 {context.currentPage}
                </span>
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="p-1 rounded hover:bg-[var(--surface-bg)] text-dim"
                >
                  <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>
            
            {/* Tabs */}
            <div className="flex gap-1 mt-3">
              <button
                onClick={() => setActiveTab('new')}
                className={`flex-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  activeTab === 'new'
                    ? 'bg-purple-500/20 text-purple-400'
                    : 'text-dim hover:text-[var(--text)]'
                }`}
              >
                New Report
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`flex-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  activeTab === 'history'
                    ? 'bg-purple-500/20 text-purple-400'
                    : 'text-dim hover:text-[var(--text)]'
                }`}
              >
                History ({reports.length})
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[50vh] md:max-h-[65vh]">
            {activeTab === 'new' ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Type Selection */}
                <div>
                  <label className="block text-xs font-medium text-sub mb-2">Type</label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {(Object.keys(typeConfig) as FeedbackType[]).map((type) => {
                      // Short labels that fit in buttons
                      const shortLabels: Record<FeedbackType, string> = {
                        BUG: 'Bug',
                        FEATURE: 'Feature',
                        IMPROVEMENT: 'Improve',
                        QUESTION: 'Question',
                      };
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setFormData({ ...formData, type })}
                          className={`p-1.5 rounded-lg border text-center transition-colors ${
                            formData.type === type
                              ? 'border-purple-500 bg-purple-500/10'
                              : 'border-[color:var(--border)] hover:border-purple-500/50'
                          }`}
                        >
                          <div className={`${typeConfig[type].color} flex justify-center mb-0.5`}>
                            {typeConfig[type].icon}
                          </div>
                          <span className="text-[10px] text-[var(--text)] leading-tight">{shortLabels[type]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-xs font-medium text-sub mb-2">Priority</label>
                  <div className="flex gap-2">
                    {(Object.keys(priorityConfig) as FeedbackPriority[]).map((priority) => (
                      <button
                        key={priority}
                        type="button"
                        onClick={() => setFormData({ ...formData, priority })}
                        className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                          formData.priority === priority
                            ? priorityConfig[priority].color
                            : 'bg-[var(--surface-bg)] text-dim hover:text-[var(--text)]'
                        }`}
                      >
                        {priorityConfig[priority].label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-xs font-medium text-sub mb-1">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Brief summary of the issue or request"
                    className="w-full px-3 py-2 text-sm bg-[var(--input-bg)] border border-[color:var(--border)] rounded-lg text-[var(--text)] placeholder-[var(--text-dim)]"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-medium text-sub mb-1">Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Detailed description..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm bg-[var(--input-bg)] border border-[color:var(--border)] rounded-lg text-[var(--text)] placeholder-[var(--text-dim)] resize-none"
                    required
                  />
                </div>

                {/* Expanded fields */}
                {isExpanded && (
                  <>
                    {formData.type === 'BUG' && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-sub mb-1">Steps to Reproduce</label>
                          <textarea
                            value={formData.steps}
                            onChange={(e) => setFormData({ ...formData, steps: e.target.value })}
                            placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
                            rows={3}
                            className="w-full px-3 py-2 text-sm bg-[var(--input-bg)] border border-[color:var(--border)] rounded-lg text-[var(--text)] placeholder-[var(--text-dim)] resize-none"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-sub mb-1">Expected</label>
                            <textarea
                              value={formData.expectedBehavior}
                              onChange={(e) => setFormData({ ...formData, expectedBehavior: e.target.value })}
                              placeholder="What should happen"
                              rows={2}
                              className="w-full px-3 py-2 text-sm bg-[var(--input-bg)] border border-[color:var(--border)] rounded-lg text-[var(--text)] placeholder-[var(--text-dim)] resize-none"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-sub mb-1">Actual</label>
                            <textarea
                              value={formData.actualBehavior}
                              onChange={(e) => setFormData({ ...formData, actualBehavior: e.target.value })}
                              placeholder="What actually happens"
                              rows={2}
                              className="w-full px-3 py-2 text-sm bg-[var(--input-bg)] border border-[color:var(--border)] rounded-lg text-[var(--text)] placeholder-[var(--text-dim)] resize-none"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* Screenshot option */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.includeScreenshot}
                        onChange={(e) => setFormData({ ...formData, includeScreenshot: e.target.checked })}
                        className="rounded border-[color:var(--border)]"
                      />
                      <Camera className="w-4 h-4 text-dim" />
                      <span className="text-xs text-dim">Include screenshot</span>
                    </label>
                  </>
                )}

                {/* Context Info */}
                <div className="p-2 rounded bg-[var(--surface-bg)] text-xs text-dim">
                  <p className="flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Context auto-captured: Page, recent actions, errors
                  </p>
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  isLoading={isSubmitting}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500"
                >
                  Create Report
                </Button>
              </form>
            ) : (
              /* History Tab */
              <div className="space-y-3">
                {reports.length === 0 ? (
                  <div className="text-center py-8 text-dim">
                    <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No reports yet</p>
                  </div>
                ) : selectedReport ? (
                  /* Selected Report Detail */
                  <div>
                    <button
                      onClick={() => setSelectedReport(null)}
                      className="text-xs text-purple-400 hover:text-purple-300 mb-3"
                    >
                      ← Back to list
                    </button>
                    
                    <div className="p-3 rounded-lg bg-[var(--surface-bg)] border border-[color:var(--border)]">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={typeConfig[selectedReport.type].color}>
                            {typeConfig[selectedReport.type].icon}
                          </span>
                          <span className="font-medium text-[var(--text)] text-sm">
                            {selectedReport.title}
                          </span>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs ${priorityConfig[selectedReport.priority].color}`}>
                          {selectedReport.priority}
                        </span>
                      </div>
                      
                      <p className="text-xs text-dim mb-3">{selectedReport.description}</p>
                      
                      <div className="text-xs text-dim mb-3">
                        <p>📍 Page: {selectedReport.context.currentPage}</p>
                        <p>📅 {new Date(selectedReport.createdAt).toLocaleString()}</p>
                        {selectedReport.branchName && (
                          <p>🌿 Branch: {selectedReport.branchName}</p>
                        )}
                      </div>

                      {/* Show stored agent/PR links if available */}
                      {(selectedReport.agentUrl || selectedReport.prUrl) && (
                        <div className="mb-3 flex flex-wrap gap-2">
                          {selectedReport.agentUrl && (
                            <a
                              href={selectedReport.agentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 text-xs"
                            >
                              <ExternalLink className="w-3 h-3" />
                              <span>Agent</span>
                            </a>
                          )}
                          {selectedReport.prUrl && (
                            <a
                              href={selectedReport.prUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-oler-blue/20 text-oler-blue-light hover:bg-oler-blue/30 text-xs"
                            >
                              <ExternalLink className="w-3 h-3" />
                              <span>PR</span>
                            </a>
                          )}
                        </div>
                      )}

                      {/* Status Messages */}
                      {cursorStatus && (
                        <div className={`mb-3 p-2 rounded text-xs ${
                          cursorStatus.success 
                            ? 'bg-success/20 text-success border border-success/30' 
                            : 'bg-danger/20 text-danger border border-danger/30'
                        }`}>
                          {cursorStatus.success ? (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Check className="w-3 h-3" />
                                <span>{cursorStatus.message || 'Sent to Cursor successfully'}</span>
                              </div>
                              {(cursorStatus.agentUrl || cursorStatus.prUrl) && (
                                <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-success/20">
                                  {cursorStatus.agentUrl && (
                                    <a
                                      href={cursorStatus.agentUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1 text-success hover:underline"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      <span>View Agent</span>
                                    </a>
                                  )}
                                  {cursorStatus.prUrl && (
                                    <a
                                      href={cursorStatus.prUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1 text-success hover:underline"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      <span>View PR</span>
                                    </a>
                                  )}
                                  {cursorStatus.branchName && (
                                    <span className="text-success/70 text-[10px]">
                                      Branch: {cursorStatus.branchName}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <AlertCircle className="w-3 h-3" />
                              <span>{cursorStatus.error || 'Failed to send to Cursor'}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleCopy(selectedReport)}
                            size="sm"
                            variant="outline"
                            leftIcon={copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                            className="flex-1"
                          >
                            {copied ? 'Copied!' : 'Copy for Cursor'}
                          </Button>
                          <Button
                            onClick={() => handleDownload(selectedReport)}
                            size="sm"
                            variant="outline"
                            leftIcon={<Download className="w-3 h-3" />}
                          >
                            .md
                          </Button>
                          <Button
                            onClick={() => handleOpenGitHub(selectedReport)}
                            size="sm"
                            variant="outline"
                            leftIcon={<ExternalLink className="w-3 h-3" />}
                          >
                            GitHub
                          </Button>
                        </div>
                        <Button
                          onClick={() => handleSendToCursor(selectedReport)}
                          size="sm"
                          variant="primary"
                          isLoading={sendingToCursor}
                          leftIcon={sendingToCursor ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                          className="w-full bg-gradient-to-r from-purple-500 to-pink-500"
                          disabled={sendingToCursor || selectedReport.status === 'SUBMITTED'}
                        >
                          {sendingToCursor 
                            ? 'Sending...' 
                            : selectedReport.status === 'SUBMITTED'
                            ? 'Sent to Cursor ✓'
                            : 'Send to Cursor'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Report List */
                  reports.slice().reverse().map((report) => (
                    <div
                      key={report.id}
                      className="p-3 rounded-lg bg-[var(--surface-bg)] border border-[color:var(--border)] hover:border-purple-500/50 cursor-pointer group transition-colors"
                      onClick={() => setSelectedReport(report)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <span className={typeConfig[report.type].color}>
                            {typeConfig[report.type].icon}
                          </span>
                          <span className="font-medium text-[var(--text)] text-sm truncate max-w-[200px]">
                            {report.title}
                          </span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(report.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/20 text-red-400 transition-opacity"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-dim">
                          {report.context.currentPage}
                        </span>
                        <span className="text-xs text-dim">•</span>
                        <span className="text-xs text-dim">
                          {new Date(report.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackButton;

